// @ts-strict-ignore
import { ProjectModel } from '../../../model/project.model';
import { SelectionModel } from '../model/selection.model';
import { StringUtils } from '@shared/util/string-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleTypeVO, ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { LocaleUtils } from '@shared/util/locale-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { InsertArticleService } from './insert-article.service';
import { Subscription } from 'rxjs';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from './selection-model.service';

export interface ArticleTypeVisibility {
  selectCheck: boolean;
  articleTypeId: number;
  articleTypeName: string;
  displayName: string;
}

@Injectable({ providedIn: 'root' })
export class ArticlesVisibilityService implements OnDestroy {
  public articleTypes: ArticleTypeVisibility[] = [];

  private _articlesLoadedSubscription: Subscription;

  public articleTypesChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private localeResourceManager: LocaleResourceManager,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private containerService: ContainerService,
    private _insertArticleService: InsertArticleService
  ) {
    this.init();
  }

  private init(): void {
    this.articleTypes = [];
    this.subscribeOnArticlesLoaded()
    // sort unique types by type display name
    this.articleTypes.sort((a, b) => StringUtils.stringCompare(a.articleTypeName, b.articleTypeName));
    this.refreshArticleTypeList();
    this.applyArticleVisibilityForAllArticleTypes();
    this.subscribeLocaleChanged();
  }

  ngOnDestroy(): void {
    this._articlesLoadedSubscription?.unsubscribe()
    this._articlesLoadedSubscription = undefined;
  }

  public resetArticleTypes(): void {
    this.articleTypes = [];
    this.emitArticleTypesChanged();
  }

  public refreshHandler(): void {
    this.refreshArticleTypeList();
    this.applyArticleVisibilityForAllArticleTypes();
  }

  public refreshArticleTypeList(): void {
    // article type ids that that exist in current article containers
    const existingArticleTypeIds: number[] = [];
    const articleContainers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    for (const articleContainer of articleContainers) {
      const articleTypeVO: ArticleTypeVO = articleContainer.article.type;
      if (articleTypeVO) {
        if (!this.isArticleTypeExists(this.articleTypes, articleTypeVO.id)) {
          const newArticleType: ArticleTypeVisibility = this.createArticleTypeVisibilityObject(articleTypeVO);
          if (newArticleType) {
            this.articleTypes.push(newArticleType);
          }
        }
        if (existingArticleTypeIds.indexOf(articleTypeVO.id) < 0) {
          existingArticleTypeIds.push(articleTypeVO.id);
        }
      }
    }

    // remove article type from articleTypes list that does not exist anymore
    for (let i = 0; i < this.articleTypes.length; i++) {
      if (existingArticleTypeIds.indexOf(this.articleTypes[i].articleTypeId) < 0) {
        this.articleTypes.splice(this.articleTypes.indexOf(this.findArticleTypeById(this.articleTypes, this.articleTypes[i].articleTypeId)), 1);
        i--;
      }
    }

    this.emitArticleTypesChanged();
  }

  private emitArticleTypesChanged(): void {
    this.articleTypesChanged.emit();
  }

  private subscribeOnArticlesLoaded(): void {
    if (this._insertArticleService.allArticlesLoaded$) {
      this._articlesLoadedSubscription = this._insertArticleService.allArticlesLoaded$.subscribe({
        next: () => this.refreshHandler(),
        error: (error) => Log.error('Error in articlesLoadedNotification: ', error)
      });
    }
  }

  private isArticleTypeExists(articleTypes: ArticleTypeVisibility[], articleTypeId: number): boolean {
    const type: ArticleTypeVisibility = this.findArticleTypeById(articleTypes, articleTypeId);
    return !!type;
  }

  private findArticleTypeById(articleTypes: ArticleTypeVisibility[], articleTypeId: number): ArticleTypeVisibility {
    for (const type of articleTypes) {
      if (type.articleTypeId === articleTypeId) {
        return type;
      }
    }
    return null;
  }

  private applyArticleVisibilityForAllArticleTypes(): void {
    if (ArrayUtils.isEmpty(this.articleTypes)) {
      return;
    }
    for (const articleType of this.articleTypes) {
      this.applyArticleVisibilityForArticleType(articleType);
    }
  }

  public applyArticleVisibilityForArticleType(articleType: ArticleTypeVisibility): void {
    if (!articleType) {
      return;
    }
    const articleTypeIsVisible: boolean = articleType.selectCheck;
    const articleUsageIdsToProcess: string[] = this.findArticleUsageIdsByArticleTypeAndVisibility(articleType, !articleTypeIsVisible);
    this.toggleArticlesVisibility(articleUsageIdsToProcess);
  }

  public showOnlySelectedArticlesHandler(): void {
    const selectedUsageIds: string[] = this.selectionModel.getArticleUsageIds();
    const allArticleUsages: ArticleUsageVO[] = this.projectModel.getAllArticleUsages();
    for (const usage of allArticleUsages) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usage.id);
      if (articleContainer) {
        if (selectedUsageIds.indexOf(usage.id) > -1) {
          articleContainer.setArticleVisibility(true);
        } else {
          articleContainer.setArticleVisibility(false);
        }
      }
    }
  }

  public showAllArticles(): void {
    for (const articleType of this.articleTypes) {
      if (!articleType.selectCheck) {
        articleType.selectCheck = true;
      }
    }
    this.applyArticleVisibilityForAllArticleTypes();
  }

  private findArticleUsageIdsByArticleTypeAndVisibility(articleType: ArticleTypeVisibility, isVisible: boolean): string[] {
    const articleUsageIds: string[] = [];
    const articleUsages: ArticleUsageVO[] = this.projectArticleService.findArticleUsagesByType(articleType.articleTypeName);
    for (const articleUsage of articleUsages) {
      if (articleUsage.articleVisible === isVisible) {
        articleUsageIds.push(articleUsage.id);
      }
    }
    return articleUsageIds;
  }

  private createArticleTypeVisibilityObject(type: ArticleTypeVO, checked: boolean = true): ArticleTypeVisibility {
    return {
      selectCheck: checked,
      articleTypeId: type.id,
      articleTypeName: type.displayName,
      displayName: this.getArticleTypeLabel(type.id, type.displayName),
    };
  }

  public toggleArticlesVisibility(articleUsageIds: string[]): void {
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return;
    }
    // handle cases with grouped articled
    articleUsageIds.forEach((usageId) => ArrayUtils.mergeArrays(articleUsageIds, this.projectArticleService.findArticleUsageIdsByGroupId(usageId)));
    this.clearSelectionIfHidden(articleUsageIds);
    const articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleUsageIds);
    for (const articleContainer of articleContainers) {
      articleContainer.toggleArticleVisibility();
    }
  }

  private clearSelectionIfHidden(articleUsageIds: string[]): void {
    const selectedUsageIds: string[] = this.selectionModel.getArticleUsageIds();
    if (ArrayUtils.sourceArrayContainsElementFromTargetArray(articleUsageIds, selectedUsageIds)) {
      this.selectionModelService.removeAllSelections();
    }
  }

  private getArticleTypeLabel(id: number, name: string): string {
    if (NumberUtils.isNotNumber(id)) {
      return '';
    }
    let result: string = LocaleUtils.getLocalizedArticleType(this.localeResourceManager, id);
    if (!result) {
      result = name;
    }
    return result;
  }

  public subscribeLocaleChanged(): void {
    this.localeResourceManager.localeChanged$.subscribe(() => this.localeChanged());
  }

  protected localeChanged(): void {
    this.articleTypes.forEach((type) => (type.displayName = this.getArticleTypeLabel(type.articleTypeId, type.articleTypeName)));
  }
}
